import styler, { Styler } from 'stylefire';
import debounce from 'lodash-es/debounce';
import physics from 'popmotion/animations/physics';
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import throttle from 'lodash-es/throttle';
import times from 'lodash-es/times';
import value, { ValueReaction } from 'popmotion/reactions/value';
import { ColdSubscription } from 'popmotion/action/types';
import { ColumnsUtils, nn } from 'module/Common';
import { Sizes } from 'module/View/Common';

type Ref = (elem: HTMLElement | null) => void;

type AnimatedValues = {
  translateX: number;
  translateY: number;
  opacity: number;
  title: number;
};

type Params = {
  descriptionRef: Ref;
  contentRef: Ref;
  headerRef: Ref;
  titleRefs: Array<Ref>;
};

type Props = {
  cols: ColumnsUtils.ColumnsResult;
  titleLinesCount: number;
  availableWidth: number;
  adminBar: boolean;
  additionTopMargin: number;
  windowWidth: number;
  ready: boolean;
  children: (params: Params) => JSX.Element;
};

type State = {};

export class PublicationScroll extends React.Component<Props, State> {
  private contentRef: HTMLElement | null = null;
  private descriptionRef: HTMLElement | null = null;
  private headerRef: HTMLElement | null = null;
  private titleRefs: Array<HTMLElement | null>;
  private contentRo: ResizeObserver;
  private headerRo: ResizeObserver;
  private contentBox: DOMRectReadOnly | null = null;
  private headerBox: DOMRectReadOnly | null = null;
  private setOverflowToken: number | null = null;

  private activeAction: ColdSubscription;
  private translateValue: ValueReaction;
  private elemStyler: Styler;
  private linesStylers: Array<Styler>;

  constructor(props: Props) {
    super(props);

    this.titleRefs = times(props.titleLinesCount, _ => null);
    this.contentRo = new ResizeObserver(
      debounce((entries, observer) => {
        if (entries.length !== 1) {
          console.warn('There should be only one entry');
          return;
        }
        this.contentBox = entries[0].contentRect;
        this.update();
      }, 100)
    );

    this.headerRo = new ResizeObserver(
      debounce((entries, observer) => {
        if (entries.length !== 1) {
          console.warn('There should be only one entry');
          return;
        }
        this.headerBox = entries[0].contentRect;
        this.udpateDescriptionSize();
        this.update();
      }, 100)
    );

    this.handleScroll = throttle(this.handleScroll.bind(this), 1000 / 60);
    this.registerDescriptionRef = this.registerDescriptionRef.bind(this);
    this.registerContentRef = this.registerContentRef.bind(this);
    this.registerHeaderRef = this.registerHeaderRef.bind(this);
    this.registerTitleRef = this.registerTitleRef.bind(this);
    this.applyTranslateValue = this.applyTranslateValue.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, false);

    const top = Sizes.langTopBarHeight;
    const left = this.props.windowWidth;

    this.translateValue = value(
      { translateY: top, translateX: left, opacity: 0, title: 400 },
      this.applyTranslateValue
    );
    this.activeAction = physics({
      velocity: 0,
      friction: 0.95,
      springStrength: 200,
      from: this.translateValue.get(),
      to: this.translateValue.get(),
      restSpeed: false,
    }).start(this.translateValue);
    // debug
    if (this.activeAction && this.activeAction.stop) {
      (window as any).stopScollAnnim = this.activeAction.stop;
    }
  }

  render() {
    const result = this.props.children({
      descriptionRef: this.registerDescriptionRef,
      contentRef: this.registerContentRef,
      headerRef: this.registerHeaderRef,
      titleRefs: times(this.props.titleLinesCount, i => (elem: HTMLElement | null) => this.registerTitleRef(i, elem)),
    });
    this.update();
    return result;
  }

  componentWillUnmount() {
    if (this.activeAction && this.activeAction.stop) {
      this.activeAction.stop();
    }
    if (this.contentRo) {
      this.contentRo.disconnect();
    }
  }

  private registerDescriptionRef(controlledRef: HTMLElement | null) {
    if (this.activeAction && this.activeAction.stop) {
      this.activeAction.stop();
    }
    if (controlledRef) {
      this.descriptionRef = controlledRef;
      if (this.descriptionRef) {
        this.descriptionRef.style.opacity = '0';
        this.elemStyler = styler(this.descriptionRef, {});
      }
      this.udpateDescriptionSize();
      this.update();
    }
  }

  private udpateDescriptionSize() {
    if (this.descriptionRef && this.headerBox) {
      this.descriptionRef.style.height =
        this.headerBox.height - Sizes.smallGutter - Sizes.headerVerticalOffset(this.props.titleLinesCount) + 'px';
    }
  }

  private registerHeaderRef(controlledRef: HTMLElement | null) {
    if (this.headerRef) {
      if (this.headerRef !== controlledRef) {
        this.headerRo.unobserve(this.headerRef);
      }
    }
    if (controlledRef) {
      this.headerRef = controlledRef;
      this.headerRo.observe(controlledRef);
    }
    this.update();
  }

  private registerTitleRef(index: number, controlledRef: HTMLElement | null) {
    if (controlledRef) {
      if (this.titleRefs[index] === null) {
        this.titleRefs[index] = controlledRef;
        controlledRef.style.opacity = '0';
      } else {
        if (this.titleRefs[index] !== controlledRef) {
          // unregister
        }
      }
    }
    const allReady = this.titleRefs.filter(v => v !== null).length === this.props.titleLinesCount;
    if (allReady) {
      this.linesStylers = this.titleRefs.map(ref => styler(nn(ref), {}));
    }
  }

  private registerContentRef(controlledRef: HTMLElement | null) {
    if (this.contentRef) {
      if (this.contentRef !== controlledRef) {
        this.contentRo.unobserve(this.contentRef);
      }
    }
    if (controlledRef) {
      this.contentRef = controlledRef;
      this.contentRo.observe(controlledRef);
    }
  }

  private applyTranslateValue(values: AnimatedValues) {
    if (this.elemStyler) {
      this.elemStyler.set(values);
    }
    if (this.linesStylers) {
      this.linesStylers.forEach((stylerEleme, index) => {
        const val = -Sizes.bigTitleTopOverflow - values.title * Math.pow(1.4, this.linesStylers.length - index - 1);
        stylerEleme.set({ translateY: val, translateX: -(val / 2), opacity: values.opacity });
      });
    }
  }

  private handleScroll() {
    this.update();
  }

  private update() {
    if (this.activeAction && this.activeAction.setSpringTarget) {
      const scroll = window.scrollY;
      const isTop = scroll < 50;

      const verticalOffset = Sizes.headerVerticalOffset(this.props.titleLinesCount);
      const horizontalOffset = 100;

      const adminBar = this.props.adminBar ? Sizes.getAdminBarHeght(this.props.windowWidth) : 0;
      const margins = Sizes.getContentMargins(this.props.windowWidth);

      let top = margins[0] + adminBar;
      const contentFixedTop = top + this.props.additionTopMargin + Sizes.getLangTopBarHeight(this.props.windowWidth);

      let descriptionHeight = window.innerHeight - top;
      let left = margins[3] + this.props.cols.position(Sizes.allColumnsMinusOne(this.props.cols.nbOfColumns));
      let title = 400;

      if (isTop) {
        top = contentFixedTop + verticalOffset - Sizes.bigTitleTopOverflow + Sizes.smallGutter;
        left = left - horizontalOffset + scroll;
        title = scroll;
        if (this.headerBox) {
          descriptionHeight = contentFixedTop + this.headerBox.height - top - Sizes.smallGutter;
        }
      }

      const opacity = this.props.ready === false ? 0 : 1;

      if (this.props.ready === false) {
        title = 400;
        left = this.props.windowWidth;
      }

      if (this.headerBox && this.contentBox) {
        const bottom = top + descriptionHeight;
        const contentBottom = contentFixedTop + this.headerBox.height + this.contentBox.height - scroll;
        if (contentBottom < bottom) {
          top = top - (bottom - contentBottom);
        }
        if (this.descriptionRef) {
          this.descriptionRef.style.height = descriptionHeight + 'px';
          // this.descriptionRef.style.zIndex = '200'; // isTop ? '500' : '200';
          const nextOverflow = isTop ? 'hidden' : 'auto';
          if (nextOverflow === 'hidden') {
            if (this.setOverflowToken !== null) {
              window.clearTimeout(this.setOverflowToken);
              this.setOverflowToken = null;
            }
            this.descriptionRef.style.overflow = nextOverflow;
          } else {
            if (this.setOverflowToken === null) {
              this.setOverflowToken = window.setTimeout(() => {
                if (this.descriptionRef) {
                  this.descriptionRef.style.overflow = 'auto';
                }
              }, 300);
            }
          }
        }
      }
      this.activeAction.setSpringTarget({ translateY: top, translateX: left, opacity, title });
    }
  }
}



// WEBPACK FOOTER //
// ./src/module/View/Publication/PublicationScroll.tsx